/**
 * Helpers para los "upgrades" (ampliaciones post-aceptación) guardados en el
 * metadata de la transacción bajo la clave `upgrades`.
 *
 * Cada upgrade es un objeto con esta forma (campos relevantes):
 *   {
 *     tipos: ['ampliar_grupo' | 'ampliar_horas' | 'extras'],
 *     grupo_seleccionado, horas_seleccionadas,
 *     extras_detalle: [{ nombre, precio }], extra_manual: { nombre, precio },
 *     importe_total,  // lo que paga el bañista; SIEMPRE incluye la comisión del bañista
 *     pagado, forma_pago, fecha_creacion, stripe_payment_link_url
 *   }
 *
 * `importe_total` ya lleva la comisión del bañista (p. ej. +20 %). Por tanto:
 *   - El bañista ve `importe_total`.
 *   - El anfitrión ve el neto: se le quita la comisión del bañista para obtener
 *     la base, y a esa base se le aplica la comisión del anfitrión (p. ej. −17 %),
 *     igual que en la reserva principal.
 */
import Decimal from 'decimal.js';

import { types as sdkTypes } from './sdkLoader';
import { unitDivisor } from './currency';
import { LINE_ITEM_CUSTOMER_COMMISSION, LINE_ITEM_PROVIDER_COMMISSION } from './types';

const { Money } = sdkTypes;

// Comisiones por defecto cuando la transacción no trae line-items de comisión
// (p. ej. reservas manuales en estado `inquire`). Coinciden con la config de
// producción (bañista +20 %, anfitrión −17 %).
export const DEFAULT_CUSTOMER_COMMISSION_PCT = 20;
export const DEFAULT_PROVIDER_COMMISSION_PCT = 17;

const toNumber = value => {
  if (value == null) return null;
  // SDK BigDecimal (e.g. line-item percentages) stores the value as a string in
  // `.value` and has no numeric valueOf, so read that explicitly.
  let raw = value;
  if (typeof value === 'object') {
    raw = value._sdkType === 'BigDecimal' || value.value != null ? value.value : value;
  }
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
};

/**
 * Devuelve el array de upgrades del metadata. Tolera que `upgrades` sea un array,
 * un string JSON, o un único objeto.
 */
export const parseUpgrades = metadata => {
  const raw = metadata?.upgrades;
  if (!raw) return [];
  let value = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch (e) {
      return [];
    }
  }
  if (Array.isArray(value)) return value.filter(u => u && typeof u === 'object');
  if (typeof value === 'object') return [value];
  return [];
};

const pctFromLineItems = (lineItems, code, fallback) => {
  const li = (lineItems || []).find(item => item.code === code && !item.reversal);
  const pct = li ? toNumber(li.percentage) : null;
  return pct == null ? fallback : Math.abs(pct);
};

/**
 * Extrae los porcentajes de comisión de los line-items de la transacción, con
 * fallback a los valores por defecto de producción.
 */
export const getCommissionRates = transaction => {
  const lineItems = transaction?.attributes?.lineItems || [];
  return {
    customerPct: pctFromLineItems(
      lineItems,
      LINE_ITEM_CUSTOMER_COMMISSION,
      DEFAULT_CUSTOMER_COMMISSION_PCT
    ),
    providerPct: pctFromLineItems(
      lineItems,
      LINE_ITEM_PROVIDER_COMMISSION,
      DEFAULT_PROVIDER_COMMISSION_PCT
    ),
  };
};

const moneyFromMajor = (decimalAmount, currency) => {
  const sub = decimalAmount
    .times(unitDivisor(currency))
    .toDecimalPlaces(0)
    .toNumber();
  return new Money(sub, currency);
};

export const isUpgradePaid = upgrade => upgrade?.pagado === true;

/**
 * Etiqueta legible de un upgrade según su tipo. Requiere `intl` para los textos
 * traducidos (`UpgradesBreakdown.*`).
 */
export const getUpgradeLabel = (upgrade, intl) => {
  const tipos = Array.isArray(upgrade?.tipos) ? upgrade.tipos : [];

  if (tipos.includes('ampliar_grupo') && upgrade.grupo_seleccionado) {
    return intl.formatMessage(
      { id: 'UpgradesBreakdown.groupLabel' },
      { grupo: upgrade.grupo_seleccionado }
    );
  }
  if (tipos.includes('ampliar_horas') && upgrade.horas_seleccionadas != null) {
    return intl.formatMessage(
      { id: 'UpgradesBreakdown.hoursLabel' },
      { horas: upgrade.horas_seleccionadas }
    );
  }

  // Extras: usar el nombre del extra manual o de los extras de catálogo.
  const names = [];
  if (upgrade.extra_manual?.nombre) {
    names.push(upgrade.extra_manual.nombre);
  }
  if (Array.isArray(upgrade.extras_detalle)) {
    upgrade.extras_detalle.forEach(e => {
      if (e?.nombre) names.push(e.nombre);
    });
  }
  return names.length
    ? names.join(', ')
    : intl.formatMessage({ id: 'UpgradesBreakdown.extraLabel' });
};

/**
 * Parsea un grupo seleccionado ("31-50", "16-20", "51+", "20") a { min, max }.
 * Devuelve null si no se reconoce.
 */
export const parseGroupRange = value => {
  if (value == null) return null;
  const s = String(value).trim();
  const plus = s.match(/^(\d+)\s*\+$/);
  if (plus) return { min: Number(plus[1]), max: 999 };
  const range = s.match(/^(\d+)\s*-\s*(\d+)$/);
  if (range) return { min: Number(range[1]), max: Number(range[2]) };
  const single = s.match(/^(\d+)$/);
  if (single) return { min: Number(single[1]), max: Number(single[1]) };
  return null;
};

/**
 * Grupo final de la reserva tras las ampliaciones: el grupo de MAYOR capacidad
 * alcanzado por las ampliaciones `ampliar_grupo` (las ampliaciones de grupo
 * suben de tramo; mostramos el más alto). Devuelve { min, max } o null.
 */
export const getGroupOverride = upgrades => {
  const groups = (upgrades || [])
    .filter(u => Array.isArray(u?.tipos) && u.tipos.includes('ampliar_grupo'))
    .map(u => parseGroupRange(u.grupo_seleccionado))
    .filter(Boolean);
  if (!groups.length) return null;
  return groups.reduce((best, g) =>
    g.max > best.max || (g.max === best.max && g.min > best.min) ? g : best
  );
};

/**
 * Horas adicionales totales sumando todas las ampliaciones `ampliar_horas`.
 */
export const getExtraHours = upgrades =>
  (upgrades || [])
    .filter(u => Array.isArray(u?.tipos) && u.tipos.includes('ampliar_horas'))
    .reduce((sum, u) => {
      const h = toNumber(u.horas_seleccionadas);
      return sum + (h || 0);
    }, 0);

/**
 * Calcula los importes a mostrar para un upgrade. Todo se deriva de
 * `importe_total`, que ya incluye la comisión del bañista.
 *  - `guest`: importe_total tal cual (lo que paga el bañista).
 *  - `base`: precio sin la comisión del bañista = importe_total / (1 + customerPct/100).
 *  - `providerFee`: tarifa del anfitrión = base * providerPct/100 (positivo).
 *  - `hostNet`: neto del anfitrión = base - providerFee, ya redondeados.
 * Devuelve `null` si no hay importe_total numérico.
 */
export const computeUpgradeAmounts = (upgrade, { currency, customerPct, providerPct }) => {
  const total = toNumber(upgrade?.importe_total);
  if (total == null) return null;
  const guestDecimal = new Decimal(total);
  const baseDecimal = guestDecimal.dividedBy(
    new Decimal(1).plus(new Decimal(customerPct).dividedBy(100))
  );
  const feeDecimal = baseDecimal.times(new Decimal(providerPct).dividedBy(100));
  const base = moneyFromMajor(baseDecimal, currency);
  const providerFee = moneyFromMajor(feeDecimal, currency);
  return {
    guest: moneyFromMajor(guestDecimal, currency),
    base,
    providerFee,
    // Del base y la tarifa ya redondeados, como Stripe: redondear el neto por su
    // cuenta puede dar un céntimo más (58,50 → tarifa 9,95 y neto 48,56, no 48,55).
    hostNet: new Money(base.amount - providerFee.amount, currency),
  };
};
